import { useQuery } from '@tanstack/react-query';
import { MedicalNote, MedicalNoteType, getMedicalNote } from '@uplift/api';
import React, { useState } from 'react';
import { Loader, UpliftThemeProvider } from '@uplift/ui-v2';
import { DischargeNoteFormProps } from './DischargeNoteForm';
import { FormStep } from './FormStep';
import { ReviewStep } from './ReviewStep';
import { useAllDiagnosisQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { Redirect } from 'react-router';
import { providerPatientRoute } from '@uplift/constants';

export type DischargeNotePageProps = {
  noteId: string;
  patientId: string;
};


export type DischargeNoteStep = 'form' | 'review';
export type DischargeNoteStepProps = {
  dischargeNote: MedicalNote<MedicalNoteType.therapyDischargeNote | MedicalNoteType.psychiatryDischargeNote>;
  step: DischargeNoteStep;
  setStep: (step: DischargeNoteStep) => void;
} & DischargeNotePageProps & Pick<DischargeNoteFormProps, 'diagnoses' | 'patientFullName'>;


export const DischargeNotePage: React.VFC<DischargeNotePageProps> = (props) => {
  const [ step, setStep ] = useState<DischargeNoteStep>('form');
  const { data: diagnoses = [], isInitialLoading: isDiagnosesLoading } = useAllDiagnosisQuery();
  const { data: displayName, isInitialLoading: isDisplayNameLoading } = useUserDisplayNameQuery(props.patientId);
  const { data: dischargeNote, isInitialLoading: isMedicalNoteLoading } = useQuery([ 'medical-notes', props.noteId ], async () => {
    const response = await getMedicalNote<MedicalNoteType.therapyDischargeNote | MedicalNoteType.psychiatryDischargeNote>(props.noteId);

    return response.data;
  }, { cacheTime: 0, retry: false });


  if ([ isDiagnosesLoading, isMedicalNoteLoading, isDisplayNameLoading ].some(Boolean)) {
    return (
      <UpliftThemeProvider>
        <Loader />
      </UpliftThemeProvider>
    );
  }

  if (!dischargeNote || dischargeNote.submittedAt) {
    return <Redirect to={providerPatientRoute.createLink({ patientId: props.patientId })} />;
  }

  const patientFullName = displayName.full;

  const Step = step === 'form' ? FormStep : ReviewStep;

  return (
    <Step
      {...props}
      dischargeNote={dischargeNote}
      patientFullName={patientFullName}
      diagnoses={diagnoses}
      step={step}
      setStep={setStep}
    />
  );
};
